import { defineStore } from "pinia";
import { ref } from "vue";
import { getDonations, fetchDonation, saveDonation, editDonation, removeDonation, fetchDonationCatalogs, fetchDonationComments, addDonationComment, editDonationComment, removeDonationComment, fetchAvailableDonationTransitions, } from "../infrastructure/adapters/donationService";
function computeCacheKey(params) {
    return JSON.stringify({
        q: params?.q ?? "",
        status: params?.status ?? "",
        paymentMethod: params?.paymentMethod ?? "",
        donationType: params?.donationType ?? "",
        statuses: params?.statuses?.join(",") ?? "",
        paymentMethods: params?.paymentMethods?.join(",") ?? "",
        donationTypes: params?.donationTypes?.join(",") ?? "",
        sources: params?.sources?.join(",") ?? "",
        withoutSource: params?.withoutSource ?? false,
        donorId: params?.donorId ?? "",
        campaignId: params?.campaignId ?? "",
        dateFrom: params?.dateFrom ?? "",
        dateTo: params?.dateTo ?? "",
        page: params?.page,
        pageSize: params?.pageSize,
        sortBy: params?.sortBy ?? "",
        sortOrder: params?.sortOrder ?? "",
        allowSearchIndex: params?.allowSearchIndex ?? false,
    });
}
/**
 * OPDON sends the donor stats currency on the detail read only: a save response carries the
 * recalculated amounts without it. The workspace currency never changes, so the one the detail
 * read gave still applies; without it the view would fall back to the donation's own currency.
 */
function withDonorStatsCurrency(donation, previousCurrency) {
    if (!donation.donorStats || donation.donorStats.currency || !previousCurrency)
        return donation;
    return { ...donation, donorStats: { ...donation.donorStats, currency: previousCurrency } };
}
export const useDonationStore = defineStore("donation", () => {
    const donations = ref([]);
    const currentDonation = ref(null);
    const catalogs = ref(null);
    const comments = ref([]);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const isLoadingComments = ref(false);
    const isSavingComment = ref(false);
    const error = ref(null);
    const availableTransitions = ref([]);
    const isLoadingTransitions = ref(false);
    const transitionsError = ref(null);
    const totalItems = ref(0);
    const totalPages = ref(0);
    const currentPage = ref(1);
    const pageSize = ref(20);
    const tabCache = ref(new Map());
    // True once a page came from OPSEARCH: the list then sends its search term to the server instead of
    // filtering the loaded rows (OP-6445). Stays false on OPDON, whose `q` matches the label only.
    const searchedByServer = ref(false);
    // R97: as-you-type search issues overlapping requests; only the latest one may write the state.
    let loadSequence = 0;
    async function loadDonations(params) {
        const sequence = ++loadSequence;
        const tabId = params?.tabId;
        const skipIfCached = params?.skipIfCached ?? false;
        const resolvedPage = params?.page ?? currentPage.value;
        const resolvedPageSize = params?.pageSize ?? pageSize.value;
        const cacheKey = computeCacheKey({ ...params, page: resolvedPage, pageSize: resolvedPageSize });
        // Check cache before fetching
        if (skipIfCached && tabId) {
            const cached = tabCache.value.get(tabId);
            if (cached && cached.cacheKey === cacheKey) {
                donations.value = cached.donations;
                totalItems.value = cached.totalItems;
                totalPages.value = cached.totalPages;
                currentPage.value = cached.currentPage;
                searchedByServer.value = cached.searchedByServer;
                // This call owns the state now (it is the latest sequence): a superseded fetch still in flight
                // will not settle isLoading, so the cache hit settles it.
                isLoading.value = false;
                error.value = null;
                return;
            }
        }
        isLoading.value = true;
        error.value = null;
        try {
            const result = await getDonations({
                q: params?.q,
                status: params?.status,
                paymentMethod: params?.paymentMethod,
                donationType: params?.donationType,
                statuses: params?.statuses,
                paymentMethods: params?.paymentMethods,
                donationTypes: params?.donationTypes,
                sources: params?.sources,
                withoutSource: params?.withoutSource,
                donorId: params?.donorId,
                campaignId: params?.campaignId,
                dateFrom: params?.dateFrom,
                dateTo: params?.dateTo,
                page: resolvedPage,
                pageSize: resolvedPageSize,
                sortBy: params?.sortBy,
                sortOrder: params?.sortOrder,
                allowSearchIndex: params?.allowSearchIndex,
            });
            if (sequence !== loadSequence)
                return; // a newer request already owns the state
            donations.value = result.result;
            totalItems.value = result.totalItems;
            totalPages.value = result.totalPages;
            currentPage.value = result.currentPage;
            searchedByServer.value = result.searchedByServer === true;
            // Store in cache
            if (tabId) {
                tabCache.value.set(tabId, {
                    donations: result.result,
                    totalItems: result.totalItems,
                    totalPages: result.totalPages,
                    currentPage: result.currentPage,
                    searchedByServer: result.searchedByServer === true,
                    cacheKey,
                });
            }
        }
        catch (err) {
            // A superseded request's failure concerns a query the user already replaced: no error, no toast.
            if (sequence !== loadSequence)
                return;
            error.value = err instanceof Error ? err.message : "error.loadFailed";
            throw err;
        }
        finally {
            if (sequence === loadSequence) {
                isLoading.value = false;
            }
        }
    }
    function invalidateTabCache() {
        tabCache.value.clear();
    }
    // Monotonic request token: each loadAvailableTransitions call claims the next id, so a
    // superseded call — donation A→B navigation while A's request is still in flight, or two
    // status-changing saves close together — can't have its late response overwrite the state
    // for the donation now on screen. Mirrors loadCommentsSeq below.
    let loadTransitionsSeq = 0;
    // CONSTRAINT: does NOT rethrow, unlike every other action here — a transitions-endpoint
    // hiccup must degrade the status dropdown (disabled, safe) and never abort the caller's
    // own load/save flow that runs this alongside loadDonation/updateDonation.
    async function loadAvailableTransitions(donationId) {
        const requestId = ++loadTransitionsSeq;
        isLoadingTransitions.value = true;
        transitionsError.value = null;
        try {
            const next = await fetchAvailableDonationTransitions(donationId);
            if (requestId !== loadTransitionsSeq)
                return; // superseded by a newer load
            availableTransitions.value = next;
        }
        catch (err) {
            if (requestId !== loadTransitionsSeq)
                return; // stale failure — ignore
            availableTransitions.value = [];
            transitionsError.value = err instanceof Error ? err.message : "error.loadFailed";
        }
        finally {
            if (requestId === loadTransitionsSeq)
                isLoadingTransitions.value = false;
        }
    }
    async function loadDonation(donationId) {
        isLoading.value = true;
        error.value = null;
        // Fired, not awaited: an independent endpoint that owns its own loading/error state and
        // never rethrows (see loadAvailableTransitions above), so it runs concurrently with the
        // donation fetch instead of serializing behind it, and can't fail this load.
        void loadAvailableTransitions(donationId);
        try {
            currentDonation.value = await fetchDonation(donationId);
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.loadFailed";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function createDonation(payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const donation = await saveDonation(payload);
            invalidateTabCache();
            return donation;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.saveFailed";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function updateDonation(donationId, payload) {
        isSaving.value = true;
        error.value = null;
        try {
            const previousStatus = currentDonation.value?.status;
            const previousStatsCurrency = currentDonation.value?.donorStats?.currency;
            const donation = await editDonation(donationId, payload);
            currentDonation.value = withDonorStatsCurrency(donation, previousStatsCurrency);
            invalidateTabCache();
            // A status-changing save moves the set of valid next transitions — refresh it. Fired
            // fire-and-forget the same way loadDonation fires it above (never rethrows, runs
            // concurrently with whatever the caller does next).
            if (donation.status !== previousStatus) {
                void loadAvailableTransitions(donationId);
            }
            return donation;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.saveFailed";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function deleteDonation(donationId) {
        isSaving.value = true;
        error.value = null;
        try {
            await removeDonation(donationId);
            donations.value = donations.value.filter((d) => d.id !== donationId);
            if (currentDonation.value?.id === donationId) {
                currentDonation.value = null;
            }
            invalidateTabCache();
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.deleteFailed";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    async function loadCatalogs() {
        if (catalogs.value)
            return;
        try {
            catalogs.value = await fetchDonationCatalogs();
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.loadFailed";
            throw err;
        }
    }
    function clearCurrentDonation() {
        currentDonation.value = null;
        comments.value = [];
        loadTransitionsSeq++; // invalidate any in-flight loadAvailableTransitions request
        availableTransitions.value = [];
        isLoadingTransitions.value = false;
        transitionsError.value = null;
    }
    // ─── Comments (OP-5091) ───────────────────────────────────────────────────────
    // Monotonic request token: each loadComments call claims the next id. Only the
    // most recent in-flight request is allowed to mutate state, so switching
    // donations rapidly can't let a slow earlier response overwrite the newer one.
    let loadCommentsSeq = 0;
    async function loadComments(donationId, page = 1, perPage = 50) {
        const requestId = ++loadCommentsSeq;
        isLoadingComments.value = true;
        error.value = null;
        try {
            const next = await fetchDonationComments(donationId, page, perPage);
            if (requestId !== loadCommentsSeq)
                return; // superseded by a newer load
            comments.value = page === 1 ? next : [...comments.value, ...next];
        }
        catch (err) {
            if (requestId !== loadCommentsSeq)
                return; // stale failure — ignore
            error.value = err instanceof Error ? err.message : "error.loadFailed";
            throw err;
        }
        finally {
            if (requestId === loadCommentsSeq)
                isLoadingComments.value = false;
        }
    }
    async function createComment(donationId, content) {
        isSavingComment.value = true;
        error.value = null;
        try {
            const created = await addDonationComment(donationId, content);
            // Timeline renders newest-first (created_at DESC), so prepend the new
            // comment instead of appending — otherwise it lands at the bottom.
            comments.value = [created, ...comments.value];
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.saveFailed";
            throw err;
        }
        finally {
            isSavingComment.value = false;
        }
    }
    async function updateComment(commentId, content) {
        isSavingComment.value = true;
        error.value = null;
        try {
            const updated = await editDonationComment(commentId, content);
            comments.value = comments.value.map((c) => (c.commentId === commentId ? updated : c));
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.saveFailed";
            throw err;
        }
        finally {
            isSavingComment.value = false;
        }
    }
    async function deleteComment(commentId) {
        isSavingComment.value = true;
        error.value = null;
        try {
            await removeDonationComment(commentId);
            comments.value = comments.value.filter((c) => c.commentId !== commentId);
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "error.deleteFailed";
            throw err;
        }
        finally {
            isSavingComment.value = false;
        }
    }
    return {
        donations,
        searchedByServer,
        currentDonation,
        catalogs,
        comments,
        isLoading,
        isSaving,
        isLoadingComments,
        isSavingComment,
        error,
        availableTransitions,
        isLoadingTransitions,
        transitionsError,
        totalItems,
        totalPages,
        currentPage,
        pageSize,
        loadDonations,
        loadDonation,
        createDonation,
        updateDonation,
        deleteDonation,
        loadCatalogs,
        clearCurrentDonation,
        invalidateTabCache,
        loadComments,
        createComment,
        updateComment,
        deleteComment,
        loadAvailableTransitions,
    };
});
